import React, { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/react-hooks";
import { Icon } from "@/components";
import {
    Badge,
    Button,
    HeaderActions,
    Heading,
    LoadingProgress,
    Modal,
    Panel,
    Table,
    TableFooter,
    Text,
} from "@/ui";
import { useNotification } from "@/hooks";
import { CSVLink } from "react-csv";
import moment from "moment";
import useMeta from "../../base/hooks/useMeta.js";
import {
    MUTATION_GENERATE_TEMP_PASS,
    QUERY_USER_LIST,
} from "../../apollo/module-operations/user";
import UserFormModal from "./UserFormModal";
import GenerateTempPasswordModal from "./GenerateTempPassWord.jsx";
import _ from "lodash";
import { headers_users } from "../../config/constant";
import styles from "../store/GeneralsForm.module.scss";
import {
    PlusIcon,
    RowActionsMenu,
    storeTableUiStyles,
} from "../store/shared/index";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 75, 100];

const ExportIcon = () => <Icon icon="export" />;

const StoreUsers = () => {
    const { user } = useMeta();
    const notify = useNotification();
    const [key, setKey] = useState(true);
    const [selection, setSelection] = useState([]);
    const [userId, setUserId] = useState(null);
    const [showUserForm, setShowUserForm] = useState(false);
    const [email, setEmail] = useState(null);
    const [newPassword, setNewPassword] = useState(null);
    const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const activeSSO = process.env.REACT_APP_AUTH_IDP_PROVIDER === "treez";

    const {
        data: stores,
        loading: storeLoading,
        networkStatus,
    } = useQuery(QUERY_USER_LIST, {
        variables: {
            account_id: user.accountId,
            store_id: user.storeId,
        },
        skip: user.loading || !user.accountId || !user.storeId,
    });

    const [mutate] = useMutation(MUTATION_GENERATE_TEMP_PASS, {
        onCompleted: resp => {
            setNewPassword(resp.generateTempPass);

            notify("Password Was Generete Successfully", "success");
        },
        onError: () => {
            notify("Updating error when generating the password", "error");
        },
    });

    const launchGeneratePassword = rowEmail => {
        setEmail(rowEmail);
        setShowGenerateConfirm(true);
    };

    const confirmGeneratePassword = () => {
        setShowGenerateConfirm(false);
        mutate({
            variables: {
                input: {
                    email,
                    account_id: user.accountId,
                    store_id: user.storeId,
                },
            },
        });
    };

    const _users = stores && stores.listUsers;
    const users = useMemo(() => _users || [], [_users]);

    const totalPages = Math.max(1, Math.ceil(users.length / pageSize) || 1);
    const currentPage = Math.min(page, totalPages);
    const pageStart = (currentPage - 1) * pageSize;
    const pageRows = useMemo(
        () => users.slice(pageStart, pageStart + pageSize),
        [users, pageStart, pageSize],
    );

    const columns = useMemo(
        () => [
            {
                id: "actions",
                header: "",
                width: 52,
                align: "center",
                renderCell: row => (
                    <span
                        className={storeTableUiStyles.rowActions}
                        onClick={event => event.stopPropagation()}
                    >
                        <RowActionsMenu
                            items={[
                                {
                                    id: "update",
                                    label: "Edit",
                                    onSelect: () => {
                                        setUserId(row.id);
                                        setShowUserForm(true);
                                    },
                                },
                                {
                                    id: "generate-password",
                                    label: "Generate Password",
                                    onSelect: () =>
                                        launchGeneratePassword(row.email),
                                },
                            ]}
                            label={
                                `${row.first_name || ""} ${
                                    row.last_name || ""
                                }`.trim() ||
                                row.email ||
                                "user"
                            }
                        />
                    </span>
                ),
            },
            {
                id: "name",
                header: "Name",
                minWidth: 180,
                renderCell: row =>
                    `${row.first_name || ""} ${row.last_name || ""}`.trim(),
            },
            {
                id: "email",
                header: "Email",
                minWidth: 220,
                renderCell: row => row.email || "",
            },
            {
                id: "status",
                header: "Status",
                minWidth: 120,
                renderCell: row => (
                    <Badge variant={row.enabled ? "primary" : "neutral"}>
                        {row.enabled ? "Active" : "No Active"}
                    </Badge>
                ),
            },
        ],
        [],
    );

    const createUser = () => {
        if (!activeSSO) {
            setUserId(null);
            setShowUserForm(true);
        } else {
            notify("Users creation is not available in SSO mode", "error");
        }
    };

    const _storeLoading = storeLoading || networkStatus === 4;

    const getUserToExport = () => {
        let data = [];
        let selectedUsers = [];

        if (!selection.length) {
            return [];
        }

        selectedUsers = _.filter(users, o =>
            _.includes(selection, String(o.id)),
        );

        _.forEach(selectedUsers, (userRow, index) => {
            const created_at = moment(userRow.created_at).format(
                "MM/DD/YYYY HH:mm",
            );
            const update_at = moment(userRow.update_at).format(
                "MM/DD/YYYY HH:mm",
            );

            data.push({
                id: index + 1,
                email: userRow.email,
                first_name: `${userRow.first_name} ${userRow.last_name}`,
                active: userRow.enabled ? "Active" : "Disabled",
                role: userRow.role || "No data",
                status: userRow.status || "No data",
                created_at: created_at,
                update_at: update_at,
            });
        });
        return data;
    };

    const handleSelectionChange = rowIds => {
        setKey(!key);
        setSelection(rowIds);
    };

    const headerActions = [
        {
            id: "export",
            label: "Export",
            icon: <ExportIcon />,
            onClick: () => {
                document.getElementById("store-users-export-csv")?.click();
            },
        },
    ];

    if (!activeSSO) {
        headerActions.push({
            id: "create",
            label: "Create User",
            icon: <PlusIcon />,
            onClick: createUser,
        });
    }

    return (
        <>
            <CSVLink
                data={getUserToExport()}
                filename={`users_${new Date().toISOString()}.csv`}
                headers={headers_users}
                id="store-users-export-csv"
                key={key}
                style={{ display: "none" }}
            />

            <Modal
                description={`Are you sure you want to generate the password for the user ${
                    email || ""
                } ?`}
                footer={
                    <>
                        <Button
                            onClick={() => setShowGenerateConfirm(false)}
                            type="button"
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmGeneratePassword}
                            type="button"
                            variant="primary"
                        >
                            Generate Password
                        </Button>
                    </>
                }
                onOpenChange={open => {
                    if (!open) setShowGenerateConfirm(false);
                }}
                open={showGenerateConfirm}
                title="Generate Password"
            >
                <Text tone="secondary" variant="body-md">
                    A temporary password will be generated for this user.
                </Text>
            </Modal>

            <Panel
                className={styles.root}
                header={
                    <div className={styles.headerBar}>
                        <div className={styles.headerText}>
                            <Heading level={4}>Users</Heading>
                            <Text tone="secondary" variant="body-md">
                                All Business Users
                            </Text>
                        </div>
                        <HeaderActions
                            actions={headerActions}
                            aria-label="Users actions"
                        />
                    </div>
                }
                showHeaderDivider
            >
                {_storeLoading ? (
                    <div className={styles.loading}>
                        <LoadingProgress
                            indeterminate
                            indicator="circular"
                            size="sm"
                            title="Loading"
                        />
                    </div>
                ) : (
                    <>
                        <Table
                            columns={columns}
                            getRowId={row => String(row.id)}
                            onSelectedRowIdsChange={handleSelectionChange}
                            rows={pageRows}
                            selectable
                            selectedRowIds={selection.map(String)}
                        />
                        <TableFooter
                            onPageChange={setPage}
                            onPageSizeChange={size => {
                                setPageSize(size);
                                setPage(1);
                            }}
                            page={currentPage}
                            pageSize={pageSize}
                            pageSizeOptions={PAGE_SIZE_OPTIONS}
                            totalPages={totalPages}
                            totalRows={users.length}
                        />
                    </>
                )}
            </Panel>

            {!activeSSO && (
                <UserFormModal
                    isOpen={showUserForm}
                    storeId={user.storeId}
                    toggle={setShowUserForm}
                    userId={userId}
                />
            )}
            <GenerateTempPasswordModal
                isOpen={newPassword != null}
                newPassword={newPassword}
                toggle={setNewPassword}
            />
        </>
    );
};

export default StoreUsers;
